/* eslint-disable react-hooks/exhaustive-deps */
import { Tree, Button, Spin, Space, Card, Popconfirm, message, Form } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

import BlogTreeNodeDetails from "./BlogTreeNodeDetails";
import BlogModal from "./Modals/BlogModal";
import { AuthorModal } from "./Modals/BlogModal/AuthorModal";
import BlogMoveModal from "./Modals/BlogMoveModal";
import FolderModal from "./Modals/FolderModal";
import ManageAuthorsModal from "./Modals/ManageAuthorsModal";
import {
  TreeContainer,
  ActionsRow,
  PublishedSpan,
  UnpublishedSpan,
  IsPublishedBadgeSpan
} from "./styles";
import { showErrorNotification } from "../../../utils/notification";

export const MODAL_CONFIG_MODES = {
  CREATE_FOLDER: "create-folder",
  EDIT_FOLDER: "edit-folder",
  CREATE_BLOG: "create-blog",
  EDIT_BLOG: "edit-blog",
  CHANGE_BLOG_PARENT: "change-blog-parent"
};

export const BLOG_PUBLISH_STATUS = {
  DRAFT: "DRAFT",
  FINALIZED: "FINALIZED",
  PUBLISHED: "PUBLISHED"
};

const BlogsDashboard = ({ ROOT_URL, BASE_URL }) => {
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [basicBlogDetailsList, setBasicBlogDetailsList] = useState([]);
  const [blogAuthors, setBlogAuthors] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [modalConfig, setModalConfig] = useState({
    open: false,
    mode: null,
    parentNode: null
  });

  const [manageAuthorsOpen, setManageAuthorsOpen] = useState(false);
  const [authorForm] = Form.useForm();
  const [authorModalOpen, setAuthorModalOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [isUploadingProfilePicture, setIsUploadingProfilePicture] = useState(false);

  const loadBlogTree = async () => {
    axios
      .get(`${ROOT_URL}/${BASE_URL}/tree-node/fetch`)
      .then(({ data }) => {
        setTreeData(data || []);
        setBasicBlogDetailsList(extractBlogListFromTree(data));
      })
      .catch(showErrorNotification);
  };

  /**
   * Extract all blog nodes from blog tree
   *
   * @param {Array<Object>} tree
   * @returns {Array<{ title: string, activeUniqueUrlIdentifier: string }>}
   */
  const extractBlogListFromTree = (tree = []) => {
    const blogs = [];
    const stack = Array.isArray(tree) ? [...tree] : [];
    while (stack.length) {
      const node = stack.pop();
      if (!node) {
        continue;
      }
      const { isFolder, title = "", activeUniqueUrlIdentifier = "", children = [] } = node;
      if (isFolder === false) {
        blogs.push({ title, activeUniqueUrlIdentifier });
      } else if (children?.length > 0) {
        stack.push(...children);
      }
    }
    // Reversed to maintain original order of blogs
    blogs.reverse();
    return blogs;
  };

  const fetchBlogAuthors = async () => {
    axios
      .get(`${ROOT_URL}/${BASE_URL}/fetch-form-requisites`)
      .then(({ data }) => setBlogAuthors(data || []))
      .catch(showErrorNotification);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadBlogTree(), fetchBlogAuthors()]).finally(() => setLoading(false));
  }, []);

  const buildBlogTree = (nodes) => {
    return nodes.map((node, index) => {
      const getFolderBadge = () => {
        if (!node?.isFolder) {
          return null;
        }
        const published = node?.publishedBlogsCount || 0,
          unpublished = node?.unpublishedBlogsCount || 0;
        const isExpandedByDefault = node?.isExpandedByDefault !== false;
        return (
          <span
            style={{
              marginLeft: "8px",
              fontSize: "11px",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <span
              style={{
                color: isExpandedByDefault ? "#1677FF" : "#8C8C8C",
                fontWeight: 500
              }}
            >
              {isExpandedByDefault ? "▾ Open" : "▸ Closed"}
            </span>
            <PublishedSpan>✓ {published}</PublishedSpan>
            <UnpublishedSpan>⚠ {unpublished}</UnpublishedSpan>
          </span>
        );
      };

      const getBlogBadge = () => {
        if (node?.isFolder) {
          return null;
        }
        const isPublished = node?.isBlogPublished;
        return (
          <IsPublishedBadgeSpan isPublished={isPublished}>
            {isPublished ? "✓ Published" : "✗ Unpublished"}
          </IsPublishedBadgeSpan>
        );
      };

      return {
        key: node?.activeUniqueUrlIdentifier + "_" + index,
        title: (
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ flexShrink: 0 }}>{node?.isFolder ? "📁 " : "📝 "}</span>
            <span
              style={{
                fontWeight: node?.isFolder ? "500" : "normal",
                flex: 1,
                minWidth: 0
              }}
            >
              {node?.title?.length > 20 ? node.title.slice(0, 20) + "…" : node?.title}
            </span>
            <span style={{ flexShrink: 0 }}>
              {node?.isFolder ? getFolderBadge() : getBlogBadge()}
            </span>
          </span>
        ),
        children: buildBlogTree(node?.children || []),
        data: node
      };
    });
  };

  const handleOpenModal = (mode, parentNode = null) => {
    setModalConfig({ open: true, mode, parentNode });
  };

  const handleModalClose = (shouldRefresh = false) => {
    setModalConfig({ open: false, mode: null, parentNode: null });
    if (shouldRefresh) {
      loadBlogTree();
    }
  };

  const handleDelete = async () => {
    try {
      await axios.post(`${ROOT_URL}/${BASE_URL}/tree-node/delete`, {
        activeUniqueUrlIdentifier: selectedNode.data.activeUniqueUrlIdentifier
      });
      message.success("Node deleted successfully");
      loadBlogTree();
      setSelectedNode(null);
    } catch (err) {
      showErrorNotification(err);
    }
  };

  const handleEditAuthor = (author) => {
    setEditingAuthor(author);
    authorForm.setFieldsValue({
      name: author.name,
      email: author.email,
      codeforcesHandle: author.codeforcesHandle,
      linkedinId: author.linkedinId,
      twitterProfileUrl: author.twitterProfileUrl,
      instagramProfileUrl: author.instagramProfileUrl,
      type: author.type,
      profilePictureUrl: author.profilePictureUrl
    });
    setProfilePictureUrl(author.profilePictureUrl);
    setAuthorModalOpen(true);
  };

  const submitBlogAuthor = async (values) => {
    try {
      if (editingAuthor) {
        await axios.put(`${ROOT_URL}/${BASE_URL}/authors/${editingAuthor._id}`, values);
        message.success("Author updated successfully");
      }
      setAuthorModalOpen(false);
      setEditingAuthor(null);
      authorForm.resetFields();
      setProfilePictureUrl(null);
      await fetchBlogAuthors();
    } catch (err) {
      showErrorNotification(err);
    }
  };

  const uploadProfilePicture = async (file) => {
    setIsUploadingProfilePicture(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const result = await axios.post(`${ROOT_URL}/${BASE_URL}/authors/upload-asset`, fd);
      const { blogAssetHostedUrl } = result.data;
      setProfilePictureUrl(blogAssetHostedUrl);
      authorForm.setFieldsValue({ profilePictureUrl: blogAssetHostedUrl });
      message.success("Profile picture uploaded!");
      return false;
    } catch (err) {
      showErrorNotification(err);
      return false;
    } finally {
      setIsUploadingProfilePicture(false);
    }
  };

  const renderModal = () => {
    if (!modalConfig.open) {
      return null;
    }

    const modalMode = modalConfig.mode;
    if (
      modalMode === MODAL_CONFIG_MODES.CREATE_FOLDER ||
      modalMode === MODAL_CONFIG_MODES.EDIT_FOLDER
    ) {
      return (
        <FolderModal
          config={modalConfig}
          onClose={handleModalClose}
          ROOT_URL={ROOT_URL}
          BASE_URL={BASE_URL}
        />
      );
    }
    if (
      modalMode === MODAL_CONFIG_MODES.CREATE_BLOG ||
      modalMode === MODAL_CONFIG_MODES.EDIT_BLOG
    ) {
      return (
        <BlogModal
          config={modalConfig}
          onClose={handleModalClose}
          loadBlogTree={loadBlogTree}
          ROOT_URL={ROOT_URL}
          BASE_URL={BASE_URL}
          blogAuthors={blogAuthors}
          refreshBlogAuthors={fetchBlogAuthors}
          basicBlogDetailsList={basicBlogDetailsList}
        />
      );
    }
    if (modalMode === MODAL_CONFIG_MODES.CHANGE_BLOG_PARENT) {
      return (
        <BlogMoveModal
          config={modalConfig}
          onClose={handleModalClose}
          ROOT_URL={ROOT_URL}
          BASE_URL={BASE_URL}
          treeData={treeData}
        />
      );
    }
    return null;
  };

  return (
    <TreeContainer>
      <div className="tree-panel">
        <ActionsRow>
          <Space wrap>
            <Button
              type="primary"
              onClick={() =>
                handleOpenModal(
                  MODAL_CONFIG_MODES.CREATE_FOLDER,
                  selectedNode?.data?.isFolder ? selectedNode.data : null
                )
              }
            >
              + Add Folder
            </Button>

            <Button onClick={() => setManageAuthorsOpen(true)}>Manage Authors</Button>

            <Button
              disabled={!selectedNode?.data?.isFolder}
              onClick={() => handleOpenModal(MODAL_CONFIG_MODES.CREATE_BLOG, selectedNode.data)}
            >
              + Add Blog
            </Button>

            {selectedNode?.data?.isFolder && (
              <Button
                onClick={() => handleOpenModal(MODAL_CONFIG_MODES.EDIT_FOLDER, selectedNode.data)}
              >
                ✏️ Edit Folder
              </Button>
            )}

            {selectedNode?.data && !selectedNode.data.isFolder && (
              <Button
                onClick={() => handleOpenModal(MODAL_CONFIG_MODES.EDIT_BLOG, selectedNode.data)}
              >
                ✏️ Edit Blog
              </Button>
            )}

            {selectedNode?.data && !selectedNode.data.isFolder && (
              <Button
                onClick={() =>
                  handleOpenModal(MODAL_CONFIG_MODES.CHANGE_BLOG_PARENT, selectedNode.data)
                }
              >
                🔀 Move Blog
              </Button>
            )}

            <Popconfirm
              title="Are you sure you want to delete this node?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
              disabled={!selectedNode}
            >
              <Button disabled={!selectedNode}>🗑 Delete</Button>
            </Popconfirm>
          </Space>
        </ActionsRow>

        {loading ? (
          <Spin />
        ) : (
          <Tree
            key={treeData?.length || 0}
            showIcon
            defaultExpandAll
            treeData={buildBlogTree(treeData)}
            onSelect={(_, info) => setSelectedNode(info.node)}
          />
        )}
      </div>

      <div className="details-panel">
        {selectedNode ? (
          <BlogTreeNodeDetails node={selectedNode.data} />
        ) : (
          <Card>
            <p>Select a node from the tree to view details.</p>
          </Card>
        )}
      </div>

      {renderModal()}

      <ManageAuthorsModal
        open={manageAuthorsOpen}
        onClose={() => setManageAuthorsOpen(false)}
        authors={blogAuthors?.authors || []}
        onEditAuthor={handleEditAuthor}
      />
      <AuthorModal
        authorModalOpen={authorModalOpen}
        setAuthorModalOpen={(open) => {
          setAuthorModalOpen(open);
          if (!open) setEditingAuthor(null);
        }}
        authorForm={authorForm}
        submitBlogAuthor={submitBlogAuthor}
        uploadProfilePicture={uploadProfilePicture}
        isUploadingProfilePicture={isUploadingProfilePicture}
        profilePictureUrl={profilePictureUrl}
        referenceData={blogAuthors}
        isEditMode={true}
      />
    </TreeContainer>
  );
};

export default BlogsDashboard;
